/**
 * The words the Rooms list and a room's page say about a room: its kind, when it runs, its card's audience line,
 * how far a draft has got, and where it opens. Pure, so the list, the page and the tests read the same rule.
 */
import { HARNESS_STEP_DONE, HARNESS_STEP_ORDER, HARNESS_STEP_REVIEW } from "../../../shared/config";
import { daysUntil, labelParts } from "../../../shared/live-time";
import { roomKindOf } from "../../../shared/room-kind";
import type {
  RoomAudienceResponse,
  RoomAudienceSession,
  RoomFace,
  RoomGroup,
  RoomGroupKey,
  WebinarDetail,
  WebinarLiveGlance,
  WebinarSummary,
} from "../../../shared/types";
import type { PillTone } from "../../components/ui/Pill";
import { boardPath } from "../../board/paths";

type RoomKind = "Weekly" | "Plays any time" | "One-time" | "Draft";

export const KIND_TONE: Record<RoomKind, PillTone> = {
  Weekly: "wine",
  "Plays any time": "brass",
  "One-time": "line",
  Draft: "warn",
};

/** A live as both the list's glance and the page's full read carry it. */
export interface LiveLike {
  state: WebinarLiveGlance["state"];
  startsAt: string;
  timezone: string;
  lengthMin: number;
  cameLive: number;
  /** The replay plays now (`replay.playable` on the page, `replayPlays` on the list's glance). */
  replayPlays?: boolean;
  series?: { chip: string; ended: boolean };
}

type Kindable = Pick<WebinarSummary, "status" | "schedule_mode"> & {
  live?: { series?: unknown } | undefined;
};

/**
 * A live's day as a card says it: "today", "tomorrow", "yesterday", the weekday within a week either side, else
 * "17 October".
 */
export function dayWord(iso: string, tz: string, nowMs: number): string {
  const d = daysUntil(iso, tz, nowMs);
  const p = labelParts(iso, tz, "long");
  if (d === null || !p) return "";
  if (d === 0) return "today";
  if (d === 1) return "tomorrow";
  if (d === -1) return "yesterday";
  return Math.abs(d) < 7 ? p.weekday : `${p.day} ${p.month}`;
}

/** "Thu 29 Oct, 6:30 PM": a one-time live's day and time. */
export function liveWhen(iso: string, tz: string): string {
  const p = labelParts(iso, tz, "short");
  return p ? `${p.weekday} ${p.day} ${p.month}, ${p.time}` : "";
}

/** What kind of room it is, in the host's words. */
export function kindOf(w: Kindable): RoomKind {
  if (w.status === "draft") return "Draft";
  return roomKindOf(w.schedule_mode, !!w.live?.series);
}

/** A one-time live that has run and whose replay plays: its replay is what people watch now. */
export function replayReady(w: { live?: LiveLike | undefined }): boolean {
  return !!w.live && !w.live.series && w.live.state === "ended" && w.live.replayPlays === true;
}

/** How long it runs, or Live while it is on air. */
export function lengthLabel(w: Pick<WebinarSummary, "duration_sec"> & { live?: LiveLike | undefined }): string {
  if (w.live) return w.live.state === "live" ? "Live" : `${w.live.lengthMin} min`;
  return w.duration_sec > 0 ? `${Math.round(w.duration_sec / 60)} min` : "";
}

/** When it runs: a series' rhythm, a live's day and time, a recording's schedule, or "Watch any time". */
export function whenLine(w: Pick<WebinarSummary, "status" | "schedule_mode" | "schedule_label"> & { live?: LiveLike | undefined }): string {
  if (w.live) {
    if (w.live.series) return w.live.series.chip;
    return liveWhen(w.live.startsAt, w.live.timezone);
  }
  if (w.status === "draft") return "Not published yet";
  if (w.schedule_mode === "on_demand") return "Watch any time";
  return w.schedule_label.trim() || "Watch any time";
}

const n = (v: number) => v.toLocaleString("en-US");

export const firstOf = (name: string): string => name.trim().split(/\s+/)[0] ?? name;

/** First names out of faces: "Megan, Noah and 3 more". */
export function namesLine(faces: readonly RoomFace[], total: number): string {
  const first = faces.slice(0, 2).map((f) => firstOf(f.name));
  const more = total - first.length;
  if (first.length === 0) return "";
  if (more <= 0) return first.join(" and ");
  return `${first.join(", ")} and ${n(more)} more`;
}

interface AudienceLine {
  strong: string;
  rest: string;
  /** Which of the two lines are about someone watching now, drawn in the watching colour. */
  strongGood: boolean;
  restGood: boolean;
  /** Everyone the face stack stands for, so it can say "+58". */
  total: number;
}

/** Who is watching a live's replay now: "Aisha is watching the replay now". */
function replayWatchers(w: Pick<WebinarSummary, "watching" | "watcher">): string {
  if (w.watching <= 0) return "";
  if (w.watching === 1 && w.watcher) return `${firstOf(w.watcher)} is watching the replay now`;
  return `${n(w.watching)} are watching the replay now`;
}

/** The card's audience line: who has a seat for the next live, who came to the last, or who is watching now. */
export function audienceLine(w: WebinarSummary, nowMs: number): AudienceLine {
  const live = w.live;
  if (live?.state === "live") {
    return { strong: "Live now", rest: `${n(live.cameLive)} here`, strongGood: true, restGood: false, total: live.cameLive };
  }
  if (live && live.state !== "ended" && !live.series?.ended) {
    const seats = live.series?.seats ?? w.registered;
    const day = dayWord(live.startsAt, live.timezone, nowMs);
    return {
      strong: `${n(seats)} registered for ${day || "the next one"}`,
      rest: replayWatchers(w),
      strongGood: false,
      restGood: true,
      total: seats,
    };
  }
  if (live) {
    const day = dayWord(live.startsAt, live.timezone, nowMs);
    // "came yesterday", but "came on Thursday": today and yesterday take no "on".
    const when = !day ? "" : day === "today" || day === "yesterday" ? ` ${day}` : ` on ${day}`;
    return {
      strong: live.series ? `${n(w.registered)} registered` : `${n(live.cameLive)} came${when}`,
      rest: replayWatchers(w),
      strongGood: false,
      restGood: true,
      total: live.series ? w.registered : live.cameLive,
    };
  }
  if (w.watching > 0) {
    return {
      strong: `${n(w.watching)} watching right now`,
      rest: namesLine(w.faces, w.watching),
      strongGood: true,
      restGood: false,
      total: w.watching,
    };
  }
  return { strong: w.registered ? `${n(w.registered)} registered` : "No one yet", rest: "", strongGood: false, restGood: false, total: w.registered };
}

/** The board's seven stops, the order a draft walks them. */
const STEP_LABEL: Record<string, string> = { titles: "Titles", promise: "Description", moments: "Moments", qa: "Q&A", offer: "Offer" };
export const BOARD_STEPS: ReadonlyArray<{ key: string; label: string }> = [
  ...HARNESS_STEP_ORDER.map((key) => ({ key, label: STEP_LABEL[key] ?? key })),
  { key: HARNESS_STEP_REVIEW, label: "Review" },
  { key: HARNESS_STEP_DONE, label: "Publish" },
];

interface DraftProgress {
  /** The step it waits at: "Moments", or "Upload your talk" before there is one. */
  next: string;
  /** 1-based; 0 before the talk is in. */
  step: number;
  total: number;
  /** Each stop: done, the one it waits at, or not reached. */
  meter: Array<"on" | "cur" | "off">;
}

/**
 * How far a draft has got, from what the board committed (`harness_step`). A recording whose video is ready has
 * had its talk read before the first step is stamped, which puts it on Titles; before that it needs its talk.
 */
export function draftProgress(
  w: Pick<WebinarSummary, "harness_step" | "harness_origin" | "video_state" | "schedule_mode">,
): DraftProgress {
  const total = BOARD_STEPS.length;
  const stamped = w.harness_step ? BOARD_STEPS.findIndex((s) => s.key === w.harness_step) : -1;
  const read = w.harness_step !== null || (w.harness_origin && w.video_state === "ready") || w.schedule_mode === "live";
  if (stamped < 0 && !read) return { next: "Upload your talk", step: 0, total, meter: BOARD_STEPS.map(() => "off") };
  const at = stamped < 0 ? 0 : stamped;
  return {
    next: BOARD_STEPS[at]?.label ?? "Titles",
    step: at + 1,
    total,
    meter: BOARD_STEPS.map((_s, i) => (i < at ? "on" : i === at ? "cur" : "off")),
  };
}

/** Where a card opens: a draft on its board, a published room on its page. */
export function roomTarget(w: Pick<WebinarSummary, "id" | "board" | "status" | "quick_live">): string {
  if (w.quick_live) return callPath(w.id);
  return w.board && w.status !== "published" ? boardPath(w.id) : `/rooms/${w.id}`;
}

/** A quick live's own page: the call, its replay, who came and the follow-up. */
export function callPath(id: string): string {
  return `/rooms/${id}/call`;
}

export type RoomFilter = "all" | "published" | "drafts";

export function filterRooms(list: readonly WebinarSummary[], f: RoomFilter): WebinarSummary[] {
  if (f === "all") return [...list];
  return list.filter((w) => (f === "drafts" ? w.status === "draft" : w.status !== "draft"));
}

/**
 * A room's page tabs: a live with sessions has its last live and its sessions too. A one-time live has only the
 * three every room has, as the design gives it, whether or not it has run.
 */
export type RoomTab = "audience" | "live" | "sessions" | "page" | "followups";

export function tabsFor(d: Pick<WebinarDetail, "live">): Array<{ key: RoomTab; label: string }> {
  const live = d.live;
  const ran = !!live?.series && live.series.sessions.some((s) => s.state === "ended");
  return [
    { key: "audience", label: "Audience" },
    ...(ran ? [{ key: "live" as const, label: "Last live" }] : []),
    ...(live?.series ? [{ key: "sessions" as const, label: "Sessions" }] : []),
    { key: "page", label: "Page and offer" },
    { key: "followups", label: "Follow-ups" },
  ];
}

export function tabPath(id: string, tab: RoomTab): string {
  return tab === "audience" ? `/rooms/${id}` : `/rooms/${id}/${tab}`;
}

/** A group's name, and what a send to it is called. */
export interface GroupLine {
  label: string;
  /** The first send to the group. */
  act: (reach: number) => string;
  /** A send after one has gone: only the people who joined the group since. */
  again: (reach: number) => string;
  subject: string;
  message: string;
}

const newOnes = (r: number) => `the ${n(r)} new ${r === 1 ? "one" : "ones"}`;

export const GROUP_WORDS: Record<RoomGroupKey, GroupLine> = {
  came: {
    label: "Came",
    act: (r) => `Thank the ${n(r)} who came`,
    again: (r) => `Thank ${newOnes(r)} who came`,
    subject: "Thanks for coming",
    message: "Thanks for coming. The replay is on your page if you want to watch a part again.",
  },
  missed: {
    label: "Missed it",
    act: (r) => `Nudge the ${n(r)} who haven't watched the replay`,
    again: (r) => `Nudge ${newOnes(r)}`,
    subject: "Still worth 20 minutes",
    message: "Sorry we missed you. The replay is on your page, whenever suits you.",
  },
  left: {
    label: "Left early",
    act: (r) => `Send the ${n(r)} who left early the part they missed`,
    again: (r) => `Send ${newOnes(r)} the part they missed`,
    subject: "The part you missed",
    message: "You left before the best part. The replay on your page starts right where you left off.",
  },
  end: {
    label: "Stayed to the end",
    act: (r) => `Thank the ${n(r)} who stayed`,
    again: (r) => `Thank ${newOnes(r)} who stayed`,
    subject: "Thank you for staying",
    message: "Thank you for staying to the end. If you'd like to talk about your plans, just reply.",
  },
  clicked: {
    label: "Clicked the offer",
    act: (r) => `Send a note to the ${n(r)} who aren't customers yet`,
    again: (r) => `Send a note to ${newOnes(r)}`,
    subject: "Your next step",
    message: "I saw you looked at my offer. If the times didn't suit you, just reply and I'll make it work.",
  },
  customers: {
    label: "Became customers",
    act: (r) => `Welcome the ${n(r)} new ${r === 1 ? "customer" : "customers"}`,
    again: (r) => `Welcome ${newOnes(r)}`,
    subject: "Before we talk",
    message: "I'm looking forward to working with you. There's a checklist on your page to look at before we start.",
  },
  asked: {
    label: "Asked a question",
    act: (r) => `Answer the ${n(r)} who asked a question`,
    again: (r) => `Answer ${newOnes(r)}`,
    subject: "About your question",
    message: "Thank you for your question. Here is my answer, and you can reply to me here any time.",
  },
  watching: { label: "Watching right now", act: () => "", again: () => "", subject: "", message: "" },
  watched_end: {
    label: "Watched to the end",
    act: (r) => `Thank the ${n(r)} who watched to the end`,
    again: (r) => `Thank ${newOnes(r)}`,
    subject: "Thank you for watching",
    message: "Thank you for watching to the end. If you'd like to talk about your plans, just reply.",
  },
  stopped_partway: {
    label: "Stopped partway",
    act: (r) => `Send the ${n(r)} who stopped partway the rest`,
    again: (r) => `Send ${newOnes(r)} the rest`,
    subject: "Pick up where you left off",
    message: "You stopped partway. It keeps your place, so it starts right where you left off.",
  },
  not_customer: {
    label: "Clicked, not a customer yet",
    act: (r) => `Send a note to the ${n(r)} who aren't customers yet`,
    again: (r) => `Send a note to ${newOnes(r)}`,
    subject: "Your next step",
    message: "I saw you looked at my offer. If the times didn't suit you, just reply and I'll make it work.",
  },
  never_came: {
    label: "Registered, never came",
    act: (r) => `Send the replay to the ${n(r)} who never came`,
    again: (r) => `Send the replay to ${newOnes(r)}`,
    subject: "It's ready when you are",
    message: "You registered but didn't get the chance to watch. It's on your page, and it keeps your place if you stop.",
  },
  regulars: { label: "Regulars", act: () => "", again: () => "", subject: "", message: "" },
  dropped: {
    label: "Dropped off",
    act: (r) => `Send the ${n(r)} a note`,
    again: (r) => `Send ${newOnes(r)} a note`,
    subject: "We miss you",
    message: "It's been a couple of sessions. The next one is soon, and the replays of the ones you missed are on your page.",
  },
  joined_late: { label: "New since the first", act: () => "", again: () => "", subject: "", message: "" },
};

/**
 * A group's words on this room. A one-time live's Came, across all time, is the people of that one day: "Came on
 * Thursday".
 */
export function groupWords(
  key: RoomGroupKey,
  scope: RoomAudienceResponse["scope"],
  detail: Pick<WebinarDetail, "title" | "live">,
  nowMs: number,
  slides = false,
): GroupLine {
  if (key !== "came") return GROUP_WORDS[key];
  const live = detail.live;
  const day = scope === "all" && live && !live.series ? dayWord(live.startsAt, live.timezone, nowMs) : "";
  const base: GroupLine = day
    ? {
        label: day === "today" || day === "yesterday" ? `Came ${day}` : `Came on ${day}`,
        act: GROUP_WORDS.came.act,
        again: GROUP_WORDS.came.again,
        subject: "Thank you for coming",
        message: `Thank you for coming to ${detail.title}. The replay is on your page if you want to watch a part again.`,
      }
    : GROUP_WORDS.came;
  if (!slides) return base;
  return {
    ...base,
    act: () => "Send everyone the slides",
    again: (r) => `Send ${newOnes(r)} the slides`,
    message: base.message.replace("The replay", "The slides are attached, and the replay"),
  };
}

/**
 * What Missed it says about the replay, from the records: when the series emailed it and how many of them pressed
 * play on it. "Replay sent Wednesday. 21 pressed play."
 */
export function missedReplayLine(played: number, mail: RoomAudienceSession["replayMail"], tz: string, nowMs: number): string {
  const pressed = played > 0 ? `${n(played)} pressed play.` : "";
  if (mail?.held) return ["The replay email is held until your email is set up.", pressed].filter(Boolean).join(" ");
  if (mail) {
    const day = dayWord(new Date(mail.at * 1000).toISOString(), tz, nowMs);
    return [`Replay sent ${day || "after the session"}.`, pressed || "No one has pressed play yet."].join(" ");
  }
  return played > 0 ? `${n(played)} pressed play on the replay.` : "";
}

/** What a group card says once a send has gone, from the server's record: "Sent today at 11:56 AM to 525". */
export function sentLine(sent: { at: number; count: number }, nowMs: number): string {
  const at = new Date(sent.at * 1000);
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const day = dayWord(at.toISOString(), zone, nowMs);
  const time = at.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `Sent ${day} at ${time} to ${n(sent.count)}`;
}

/** Why a send reaches fewer than its group: the people it leaves out. */
export function leftOutLine(unreachable: number): string {
  return unreachable > 0 ? `Leaves out ${n(unreachable)} who unsubscribed or bounced.` : "";
}

type StateTone = "hot" | "good" | "warn" | "";

/** What the room is doing now, for the line under its name in Season: a live to come, on air, a replay, who watches. */
export function stateLine(
  w: { status: string; live?: LiveLike | undefined; watching: number },
  nowMs: number,
): { text: string; tone: StateTone } | null {
  if (w.status === "draft") return null;
  const live = w.live;
  if (live?.state === "live") return { text: "Live now", tone: "good" };
  if (live && live.state !== "ended" && !live.series?.ended) {
    const day = dayWord(live.startsAt, live.timezone, nowMs);
    const time = labelParts(live.startsAt, live.timezone, "long")?.time;
    return { text: `Next live${day ? ` ${day}` : ""}${time ? `, ${time}` : ""}`, tone: "hot" };
  }
  if (live) {
    if (replayReady({ live })) return { text: "Replay ready", tone: "" };
    return live.series ? null : { text: `${n(live.cameLive)} came`, tone: "" };
  }
  return w.watching > 0 ? { text: `${n(w.watching)} watching now`, tone: "good" } : null;
}

/** The second thing on a row: who registered, or how long the talk is when no one has yet. */
export function rowLine(w: Pick<WebinarSummary, "registered" | "duration_sec">): string {
  if (w.registered > 0) return `${n(w.registered)} registered`;
  return w.duration_sec > 0 ? `${Math.round(w.duration_sec / 60)} min talk` : "No one yet";
}

/** The Rooms list's line under its title in Season, where the count is already in the title. */
export function peopleLine(list: readonly WebinarSummary[]): string {
  const people = list.reduce((sum, w) => sum + w.registered, 0);
  return people ? `${n(people)} ${people === 1 ? "person" : "people"} registered.` : "";
}

/** A room's sources: the five that brought most people, each with its share and its bar against the biggest. */
export function sourceRows(sources: ReadonlyArray<{ source: string; detail: string | null; registered: number }>) {
  const total = sources.reduce((sum, s) => sum + s.registered, 0);
  const rows = sources
    .filter((s) => s.registered > 0)
    .toSorted((a, b) => b.registered - a.registered)
    .slice(0, 5);
  const top = rows[0]?.registered ?? 0;
  return rows.map((s) => ({
    key: `${s.source}:${s.detail ?? ""}`,
    label: s.detail ? `${s.source}, ${s.detail}` : s.source,
    percent: total ? Math.round((s.registered / total) * 100) : 0,
    bar: top ? Math.round((s.registered / top) * 100) : 0,
  }));
}

/** The words under a group's name, when the room has something worth adding to its count. */
export function groupLine(
  g: RoomGroup,
  all: readonly RoomGroup[],
  detail: Pick<WebinarDetail, "live">,
  scope: RoomAudienceResponse["scope"],
): string {
  if (g.key === "clicked") {
    const customers = all.find((x) => x.key === "customers")?.count ?? 0;
    return customers ? `${n(customers)} became ${customers === 1 ? "a customer" : "customers"}.` : "";
  }
  if (g.key === "watching") return g.count ? `${namesLine(g.faces, g.count)}.` : "";
  if (g.key === "left" && g.beforeOffer !== undefined) return `Before the offer at ${n(g.beforeOffer)} min.`;
  // Someone who missed the live and is watching its replay right now, as the design says it.
  if (g.key === "missed" && g.watching > 0) {
    return `${namesLine(g.watchers, g.watching)} ${g.watching === 1 ? "is" : "are"} watching the replay now.`;
  }
  if (g.key === "came" && scope === "all" && detail.live && !detail.live.series) {
    return `${n(detail.live.stayedToEnd)} stayed to the end.`;
  }
  return "";
}

/** A draft's line: how long its talk is and when it was last touched, "35 min talk · updated 3 days ago". */
export function draftLine(w: { duration_sec: number; updated_at: number }, nowMs: number): string {
  const updated = Math.max(0, Math.round((nowMs / 1000 - w.updated_at) / 86400));
  return [
    w.duration_sec > 0 ? `${Math.round(w.duration_sec / 60)} min talk` : "",
    updated === 0 ? "updated today" : `updated ${updated} ${updated === 1 ? "day" : "days"} ago`,
  ]
    .filter(Boolean)
    .join(" · ");
}
